import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const ANCHOR_OBSERVER_TIMEOUT_MS = 3000;
const SCROLL_OPTIONS = {
    behavior: "smooth",
    block: "center",
};

const getRawHashId = (hash) => String(hash || "").replace(/^#/, "");

const getHashTargetIds = (hash) => {
    const rawId = getRawHashId(hash);
    if (!rawId) return [];

    try {
        const decodedId = decodeURIComponent(rawId);
        return decodedId === rawId ? [rawId] : [decodedId, rawId];
    } catch (_error) {
        return [rawId];
    }
};

const findByBlockDataAttribute = (targetIds) => {
    const candidates = document.querySelectorAll(
        "[data-block-id], [data-database-block-id], [data-index-block-id]",
    );

    return Array.from(candidates).find((element) => targetIds.some((targetId) => (
        element.getAttribute("data-block-id") === targetId ||
        element.getAttribute("data-database-block-id") === targetId ||
        element.getAttribute("data-index-block-id") === targetId
    ))) || null;
};

const findHashTarget = (hash) => {
    const targetIds = getHashTargetIds(hash);
    if (!targetIds.length) return null;

    for (const targetId of targetIds) {
        const element = document.getElementById(targetId);
        if (element) return element;
    }

    return findByBlockDataAttribute(targetIds);
};

function ScrollToAnchor() {
    const location = useLocation();

    useEffect(() => {
        if (!location.hash) return undefined;

        let observer = null;
        let timeoutId = null;
        let disposed = false;

        const disconnectObserver = () => {
            observer?.disconnect();
            observer = null;
            if (timeoutId) {
                window.clearTimeout(timeoutId);
                timeoutId = null;
            }
        };

        const scrollToTarget = () => {
            if (disposed) return true;
            const target = findHashTarget(location.hash);
            if (!target) return false;

            target.scrollIntoView(SCROLL_OPTIONS);
            disconnectObserver();
            return true;
        };

        if (scrollToTarget()) {
            return () => {
                disposed = true;
            };
        }

        if (typeof MutationObserver === "undefined" || !document.body) {
            return () => {
                disposed = true;
            };
        }

        observer = new MutationObserver(scrollToTarget);
        observer.observe(document.body, { childList: true, subtree: true });
        timeoutId = window.setTimeout(disconnectObserver, ANCHOR_OBSERVER_TIMEOUT_MS);

        return () => {
            disposed = true;
            disconnectObserver();
        };
    }, [location.hash, location.pathname, location.search]);

    return null;
}

export default ScrollToAnchor;
